import { Divider, Stack, type Theme } from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import type { ReactNode } from 'react';

interface PageContainerWithSidebarProps {
    /**
     * Content to display in the left sidebar
     */
    sidebar: ReactNode;

    /**
     * Main content area
     */
    children: ReactNode;

    /**
     * Optional bottom actions bar (e.g., save buttons)
     */
    actions?: ReactNode;

    /**
     * Optional header to display above the sidebar
     */
    sidebarHeader?: ReactNode;

    /**
     * Whether to add a divider between sidebar sections
     * @default true
     */
    sidebarDivider?: boolean;

    /**
     * Custom sidebar width breakpoints
     * @default { xs: 12, sm: 6, md: 4, lg: 3 }
     */
    sidebarWidth?: {
        xs?: number;
        sm?: number;
        md?: number;
        lg?: number;
        xl?: number;
    };

    /**
     * Height offset for the main container (to account for bottom actions)
     * @default 85
     */
    bottomOffset?: number;

    /**
     * Whether sidebar should have a border on the right
     * @default true
     */
    sidebarBorder?: boolean;
}

/**
 * Reusable page layout with a sidebar on the left and main content on the right.
 * Commonly used for settings pages, forms with navigation, and other split-view layouts.
 *
 * @example
 * ```tsx
 * <PageContainerWithSidebar
 *   sidebarHeader={<BackButton />}
 *   sidebar={<NavigationMenu />}
 *   actions={<SaveButton />}
 * >
 *   <FormContent />
 * </PageContainerWithSidebar>
 * ```
 */
const PageContainerWithSidebar = ({
    sidebar,
    children,
    actions,
    sidebarHeader,
    sidebarDivider = true,
    sidebarWidth = { xs: 12, sm: 6, md: 4, lg: 3 },
    bottomOffset = 85,
    sidebarBorder = true,
}: PageContainerWithSidebarProps) => {
    return (
        <Stack sx={{ height: '100%' }}>
            <Grid2
                container
                sx={{ height: { sm: actions ? `calc(100% - ${bottomOffset}px)` : '100%' }, overflow: 'hidden' }}
            >
                {/* Sidebar */}
                {sidebar && (
                    <Grid2
                        xs={sidebarWidth.xs ?? 12}
                        sm={sidebarWidth.sm ?? 6}
                        md={sidebarWidth.md ?? 4}
                        lg={sidebarWidth.lg ?? 3}
                        xl={sidebarWidth.xl}
                        sx={{
                            borderRight: sidebarBorder
                                ? (theme: Theme) => `1px solid ${theme.palette.divider}`
                                : 'none',
                            height: '100%',
                            overflow: 'auto',
                        }}
                    >
                        {sidebarHeader || sidebarDivider ? (
                            <Stack divider={sidebarDivider ? <Divider /> : undefined} sx={{ height: '100%' }}>
                                {sidebarHeader}
                                {sidebar}
                            </Stack>
                        ) : (
                            sidebar
                        )}
                    </Grid2>
                )}

                {/* Main Content */}
                <Grid2 xs sx={{ height: '100%', overflow: 'auto' }}>
                    {children}
                </Grid2>
            </Grid2>

            {/* Bottom Actions */}
            {actions && actions}
        </Stack>
    );
};

export default PageContainerWithSidebar;
